import React, { FC, useCallback } from "react"
import { Box, Grid, Typography } from "@mui/material"
import { useNavigate, useParams } from "react-router-dom"
import {
  HasSidebarLayout,
  DataLoad,
  CollapsibleSection,
  EntityHeader,
  WagnerProbeInfo,
  WagnerProbeChartTabs,
  WagnerProbeLastReadings,
  DisconnectProbeModal,
  RapidRhProbeEvents,
  DownloadMeasurementsModal,
} from "src/components"
import { useDeleteRapidRhProbeRequest, useWagnerProbeSingleRequest } from "src/hooks/api"
import { WagnerProbe } from "src/api"
import { useAddPopupMessage, useDialog } from "src/hooks/ui"
import { DialogNames } from "src/enums"

interface WagnerProbeShowContentProps {
  wagnerProbe: WagnerProbe | undefined;
  refetchWagnerProbe?: () => void;
}

const WagnerProbeShowContent: FC<WagnerProbeShowContentProps> = ({ wagnerProbe, refetchWagnerProbe }) => {
  const dialogName = DialogNames.GetSensorHistoricalData
  const { closeDialog } = useDialog(dialogName)
  const addMessage = useAddPopupMessage()
  const navigate = useNavigate()
  const deleteRapidRhProbeRequest = useDeleteRapidRhProbeRequest()

  const isOnlineProbe = useCallback(() => {
    let result: boolean | null = null
    if (wagnerProbe?.currentData?.readingTime) {
      const twoHours = 1000 * 60 * 60 * 2 // two hours in ms
      const differnce = Date.now() - new Date(wagnerProbe?.currentData?.readingTime).getTime()
      result = differnce > 0 && differnce <= twoHours
    }
    return result
  }, [wagnerProbe])()
  if (!wagnerProbe) return null

  const deleteProbe = (probeId: string): void => {
    deleteRapidRhProbeRequest.mutate({
      probeId
    }, {
      onSuccess: () => {
        addMessage({ text: 'The probe deleted successfully', type: 'success' })
        closeDialog()
        navigate(-1)
      },
      onError: ({ message }: { message: string }) => {
        addMessage({ text: message, type: 'error' })
      }
    })
  }

  const title = `Wagner Probe: ${wagnerProbe.probeId}`
  return (
    <>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <EntityHeader
            title={title}
            rightIcon={
              <Box
                sx={{
                  width: 15,
                  height: 15,
                  borderRadius: "50%",
                  display: isOnlineProbe === null ? "none" : "inline-block",
                  backgroundColor: isOnlineProbe ? "green" : "red",
                  borderColor: isOnlineProbe ? "green" : "red",
                  ...(isOnlineProbe ? { animation: "pulsate 1.1s infinite alternate" } : {}),
                  "@keyframes pulsate": {
                    from: {
                      opacity: 1,
                    },
                    to: {
                      opacity: 0.1,
                    }
                  }
                }}
              />
            }
            actionBlockOnTheRight={<DisconnectProbeModal
              rapidRhProbe={wagnerProbe}
              onDisconnectProbe={deleteProbe}
              isLoading={false}
            />}
          >
            <Grid container spacing={2}>
              <Grid item>
                <DownloadMeasurementsModal deviceType="wagnerProbe" deviceId={wagnerProbe.probeId} />
              </Grid>
            </Grid>
          </EntityHeader>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Wagner Probe Information
          </Typography>
          <WagnerProbeInfo wagnerProbe={wagnerProbe} refetchWagnerProbe={refetchWagnerProbe} />
        </Grid>
        <Grid item xs={12}>
          <CollapsibleSection title="Wagner Probe Chart Of Readings">
            <WagnerProbeChartTabs wagnerProbe={wagnerProbe} />
          </CollapsibleSection>
        </Grid>
        <Grid item xs={12}>
          <CollapsibleSection title="Events">
            <RapidRhProbeEvents rapidRhProbeId={wagnerProbe.id} />
          </CollapsibleSection>
        </Grid>
        <Grid item xs={12}>
          <CollapsibleSection title="Wagner Probe Last Readings">
            <WagnerProbeLastReadings wagnerProbe={wagnerProbe} />
          </CollapsibleSection>
        </Grid>
      </Grid>
    </>
  )
}

const WagnerProbeShow: FC = () => {
  const params = useParams()
  const { probeId } = params as { probeId: string }
  const { data, isInitialLoading, isError, error, refetch } = useWagnerProbeSingleRequest({ probeId })
  document.title = `CLīMIT Admin Panel - Rapid RH probe ${data?.probeId || ''}`

  return (
    <HasSidebarLayout>
      <DataLoad
        isLoading={isInitialLoading}
        isError={isError}
        errorMessage={error?.message}
      >
        <WagnerProbeShowContent wagnerProbe={data} refetchWagnerProbe={refetch} />
      </DataLoad>
    </HasSidebarLayout>
  )
}

export default WagnerProbeShow
